<template>
    <md-card class="ql-page" id="ql-page">
        <md-card-media-cover :id="`cardCover${pageNumber}`" ref="cardCover" class="ql-page-cover">
            <project-pdf-page class="ql-pdf-page" :class="{'night-mode': showMode, 'disable-text': disableText}"
                v-if="ready"
                :page="page" 
                :positionedCues="positionedCues"
                :pageWidth="pageWidth"
                :pageHeight="pageHeight"
                :currentCueLayer="currentCueLayer"
                @pdfLoaded="pdfLoaded"
                :issrc="issrc"
                :svgTrueHeight="svgTrueHeight"
                @openDialog="openDialog"
            />
            <p v-if="!ready">Loading PDF...</p>
            <resize-sensor @resize="resize"></resize-sensor>
        </md-card-media-cover>
        <div class="marker-container" v-if="pageReady" :style="{left: `${offsetX}px`, width: `${pdfWidth}px`}">
            <cue-position-marker v-for="cue in positionCues"
                :cue="cue"
                :key="`${cue.id}${versionID}`"
                :editable="false"
                :locked="locked"
                :pageWidth="pageWidth"
                :pageHeight="pageHeight"
            />
            <cue-selection-marker v-for="cue in selectionCues"
                :cue="cue"
                :key="`${cue.id}${versionID}`"
                :editable="false"
                :locked="locked"
                :pageWidth="pageWidth"
                :pageHeight="pageHeight"
            />
            <script-marker-overlay
                v-for="marker in scriptMarkersOnPage"
                :key="`${marker.markerId || marker.id}-${pageWidth}-${pageHeight}`"
                :marker="marker"
                :editable="false"
                :locked="true"
                :pageWidth="pageWidth"
                :pageHeight="pageHeight"
                :scale="Number(selectedScale)"
            />
        </div>

        <div class="ql-note-container" v-if="pageReady" :style="{left: `${offsetX}px`, width: `${pdfWidth}px`}">
            <project-note-template
                v-for="note in displayNotes"
                :key="`${note.id}${versionID}`"
                :note="note"
                :pageWidth="pageWidth"
                :pageHeight="pageHeight"
                :totalPages="totalPages"
                :selectedElement="selectedElement"
            />
        </div>
    </md-card>
</template>

<script>
// Vendor
import resizeSensor from 'vue-resize-sensor';
import { mapGetters } from 'vuex';
// Component
import CuePositionMarker from '@/components/print/cues/cue-position-marker';
import CueSelectionMarker from '@/components/print/cues/cue-selection-marker';
import ProjectCueList from '@/components/print/cues/cue-list';
import ProjectPdfPage from '@/components/print/pdf-page';
import ProjectNoteTemplate from '@/components/print/notes/wrapper';
import ScriptMarkerOverlay from '@/components/project/markers/script-marker';

const ProjectPage = {
    name: 'projectPrintPage',
    props: ['page', 'layers', 'currentCueLayer', 'cues', 'locked', 'showMode', 'totalPages', 'landscape', 'issrc', 'positionedCues', 'svgTrueHeight', 'selectedElement'],
    data: function() {
        return {
            pageNumber: this.page.pageNumber,
            pageContainerId: 'pageContainer' + this.page.pageNumber,
            ready: false,
            pageReady: false,
            disableText: false,
            addingCue: false,
            addCueX: 0,
            addCueY: 0,
            cueToAdd: null,
            offsetX: 0,
            offsetY: 0,
            pageWidth: 0,
            pageHeight: 0,
            pdfHeight: 0,
            pdfWidth: 0,
            originWidth: 0,
            originHeight: 0,
            cueMarkerWidth: 6,
            cueMarkerHeight: 6,
            selectedScale: localStorage.getItem('page-print-scale') || 1,
            isPrinting: false
        };
    },
    computed: {
        ...mapGetters([
            'currentVersion',
            'layerByCue',
            'visibleCues',
            'editableLayers',
            'notesByPage',
            'cuePlacement',
            'visibleScriptMarkersByPage'
        ]),
        versionID: function() {
            return this.currentVersion ? this.currentVersion.id : '';
        },
        displayCues: function() {
            return this.cues;
        },
        selectionCues: function() {
            return this.displayCues.filter(c => c.type === 'selection');
        },
        positionCues: function() {
            return this.displayCues.filter(c => c.type === 'position');
        },
        displayNotes: function() {
            return this.notesByPage(this.pageNumber);
        },
        scriptMarkersOnPage: function() {
            return this.visibleScriptMarkersByPage(this.pageNumber);
        }
    },
    methods: {
        openDialog(ref) {
            this.$refs[ref].open();
        },
        closeDialog(ref) {
            this.$refs[ref].close();
        },
        getPDFViewport() {
            return this.page.pdfPage.getViewport(this.selectedScale, this.page.pdfPage.pageInfo.rotate);
        },
        readyPage() {
            this.ready = false;
            const bounds = this.$el.querySelector('.ql-page-cover').getBoundingClientRect();
            const vp = this.getPDFViewport();
            const ph = Math.max(bounds.height, vp.height);
            const pw = Math.max(bounds.width, vp.width);
            this.originWidth = vp.width;
            this.originHeight = vp.height;
            this.pageWidth = pw;
            this.pageHeight = ph;
            this.ready = true;
        },
        setScale(selectedScale) {
            this.selectedScale = selectedScale;
        },
        pdfLoaded(data) {
            this.setOffset();
            this.setScale(data.viewport.scale);
            this.$nextTick(() => {
                this.$emit('pageLoaded', {
                    ...data,
                    offsetX: this.offsetX, 
                    offsetY: this.offsetY,
                    width: this.pdfWidth,
                    height: this.pdfHeight,
                    pageWidth: this.pageWidth,
                    pageHeight: this.pageHeight,
                    markerWidth: this.cueMarkerWidth,
                    cueMarkerHeight: this.cueMarkerHeight
                });
                this.pageReady = true;
            });
        },
        sortCues() {
            if (this.cues) {
                this.cues.sort(this.comparator);
            }
        },
        comparator(a, b) {
            if (a.y === b.y) {
                if (a.x === b.x || a.x > b.x) {
                    return 1;
                }
                return 0;
            } else if (a.y > b.y) {
                return 1;
            }
            return 0;
        },

        cueColor(cue) {
            const layer = this.layerByCue(cue);
            if (layer) return layer.color;

            return 'black';
        },
        setOffset() {
            const page = this.$el.querySelector('.ql-pdf-page');
            if (page) {
                const bounds = page.getBoundingClientRect();

                this.pdfHeight = bounds.height;
                this.pdfWidth = bounds.width;
                this.offsetY = bounds.y;

                this.$store.dispatch('updatePageOffset', bounds.x);
            }
        },
        resize() {
            // During the print window a stray resize (the print-time @page/zoom
            // relayout can trip the resize-sensor) would re-run setOffset against
            // the transformed box and corrupt the cached offsets the overlay uses.
            // Freeze offset updates while printing. The window flag is set by
            // print()/printVersion() before it applies the print-time zoom (whose
            // relayout can fire this sensor before `beforeprint` lands); the
            // beforeprint listener covers browser-initiated prints (Ctrl+P).
            if (this.isPrinting || window.qlIsPrinting) {
                return;
            }
            this.setOffset();
            if (this.pageReady) {
                this.$nextTick(() => {
                    this.$emit('pageLoaded', {
                        pageNumber: this.pageNumber,
                        selectedScale: this.selectedScale,
                        offsetX: this.offsetX, 
                        offsetY: this.offsetY,
                        width: this.pdfWidth,
                        height: this.pdfHeight,
                        pageWidth: this.pageWidth,
                        pageHeight: this.pageHeight,
                        markerWidth: this.cueMarkerWidth,
                        cueMarkerHeight: this.cueMarkerHeight
                    });
                });
            }
        }
    },
    components: {
        'cue-position-marker': CuePositionMarker,
        'cue-selection-marker': CueSelectionMarker,
        'project-cue-list': ProjectCueList,
        'resize-sensor': resizeSensor,
        'project-pdf-page': ProjectPdfPage,
        'project-note-template': ProjectNoteTemplate,
        'script-marker-overlay': ScriptMarkerOverlay
    },
    mounted: function() {
        this.readyPage();
        this.beforePrintHandler = () => { this.isPrinting = true; };
        this.afterPrintHandler = () => { this.isPrinting = false; };
        window.addEventListener('beforeprint', this.beforePrintHandler);
        window.addEventListener('afterprint', this.afterPrintHandler);
    },
    beforeDestroy: function() {
        if (this.beforePrintHandler) window.removeEventListener('beforeprint', this.beforePrintHandler);
        if (this.afterPrintHandler) window.removeEventListener('afterprint', this.afterPrintHandler);
    }
};

export default ProjectPage;
</script>

<style lang="scss">
.app-print-mode {
    .marker-container, .ql-note-container {
        position: absolute;
        width: 100%;
        height: 100%;
        z-index: 1001;
        pointer-events: none;
    }
    .marker-container {
        z-index: 1002 !important;
    }
    /* Cue markers print above structure marks (z-index 100). Print is not
       interactive, so a plain z-index is enough here. */
    .marker-container > .ql-position-cue-marker,
    .marker-container > .ql-selection-cue-marker {
        z-index: 105 !important;
    }
    .ql-page {
        width: 100%;
        position: relative !important;
        box-shadow: none;
    }
    .ql-add-cue-button {
        position: absolute;
        z-index: 1001;
    }

    .ql-pdf-page {
        margin: 0 auto;
        position: relative;
    }

    .night-mode {
        filter: invert(88%) !important;
        -webkit-filter: invert(88%) !important;
    }

    .disable-text {
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        -khtml-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
    }
}
</style>


// WEBPACK FOOTER //
// print-page.vue?909abe06